// Wires the header search input to Shopify's predictive search JSON API
// (/search/suggest.json) and renders grouped results (products, collections,
// pages, blog posts) into a dropdown below the input as the visitor types,
// debounced. Renders client-side rather than via server-rendered sections,
// since that path requires the Search & Discovery app to be installed.
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;
const RESOURCE_LIMIT = 4;

function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value ?? '';
  return div.innerHTML;
}

// The predictive search JSON API returns raw, unformatted numeric price
// strings (e.g. "49.99") with no currency symbol — hardcoding "$" would
// break every non-USD store. Format using the shop's own money_format
// (Shopify admin > Settings > Store details), same as Shopify.formatMoney.
function formatMoney(price, format) {
  if (price == null || format == null) return price;
  const cents = Math.round(parseFloat(price) * 100);
  if (isNaN(cents)) return price;

  function defaultTo(value, fallback) {
    return value == null || isNaN(value) ? fallback : value;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ',');
    decimal = defaultTo(decimal, '.');
    if (isNaN(number)) return 0;

    const negative = number < 0 ? '-' : '';
    const stringNumber = Math.abs(number / 100).toFixed(precision);
    const parts = stringNumber.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    const cents2 = parts[1] ? decimal + parts[1] : '';

    return `${negative}${dollars}${cents2}`;
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholderRegex);
  if (!match) return format.replace(placeholderRegex, formatWithDelimiters(cents, 2));

  let value;
  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return format.replace(placeholderRegex, value);
}

function renderGroup(heading, items, renderItem, gridClass) {
  if (!items || !items.length) return '';
  return `
    <div class="predictive-search__group">
      <p class="predictive-search__group-heading">${escapeHtml(heading)}</p>
      <div class="${gridClass || 'predictive-search__list'}">
        ${items.map(renderItem).join('')}
      </div>
    </div>
  `;
}

function renderProductItem(product, moneyFormat) {
  const image = product.image
    ? `<span class="predictive-search__item-image"><img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy"></span>`
    : '';
  const showPrice = !(product.tags || []).includes('quote-only');
  const price = showPrice && product.price != null
    ? `<span class="predictive-search__item-price">${escapeHtml(formatMoney(product.price, moneyFormat))}</span>`
    : '';
  return `
    <a href="${escapeHtml(product.url)}" class="predictive-search__item">
      ${image}
      <span class="predictive-search__item-body">
        <span class="predictive-search__item-title">${escapeHtml(product.title)}</span>
        ${price}
      </span>
    </a>
  `;
}

function renderSimpleItem(item) {
  return `
    <a href="${escapeHtml(item.url)}" class="predictive-search__item">
      <span class="predictive-search__item-body">
        <span class="predictive-search__item-title">${escapeHtml(item.title)}</span>
      </span>
    </a>
  `;
}

function renderArticleItem(article) {
  const image = article.image
    ? `<span class="predictive-search__item-image"><img src="${escapeHtml(article.image)}" alt="${escapeHtml(article.title)}" loading="lazy"></span>`
    : '';
  return `
    <a href="${escapeHtml(article.url)}" class="predictive-search__item">
      ${image}
      <span class="predictive-search__item-body">
        <span class="predictive-search__item-title">${escapeHtml(article.title)}</span>
      </span>
    </a>
  `;
}

function renderResults(results, query, searchUrl, labels, moneyFormat) {
  const products = results.products || [];
  const collections = results.collections || [];
  const pages = results.pages || [];
  const articles = results.articles || [];

  if (!products.length && !collections.length && !pages.length && !articles.length) {
    return `<p class="predictive-search__empty">${escapeHtml(labels.noResults)}</p>`;
  }

  const groupsHtml = [
    renderGroup(labels.products, products.slice(0, RESOURCE_LIMIT), (product) => renderProductItem(product, moneyFormat), 'predictive-search__list predictive-search__list--2col'),
    renderGroup(labels.collections, collections.slice(0, RESOURCE_LIMIT), renderSimpleItem),
    renderGroup(labels.pages, pages.slice(0, RESOURCE_LIMIT), renderSimpleItem),
    renderGroup(labels.articles, articles.slice(0, RESOURCE_LIMIT), renderArticleItem),
  ].join('');

  const viewAllUrl = `${searchUrl}?q=${encodeURIComponent(query)}&type=product,article,page`;

  return `
    <div class="predictive-search__results">
      ${groupsHtml}
      <div class="predictive-search__view-all-wrap">
        <a href="${viewAllUrl}" class="predictive-search__view-all">
          ${escapeHtml(labels.viewAll)}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
        </a>
      </div>
    </div>
  `;
}

function initPredictiveSearch(root) {
  root.querySelectorAll('[data-predictive-search]').forEach((wrapper) => {
    const input = wrapper.querySelector('[data-predictive-search-input]');
    const resultsEl = wrapper.querySelector('[data-predictive-search-results]');
    const endpoint = wrapper.dataset.predictiveSearchUrl;
    const searchUrl = wrapper.dataset.searchUrl;
    const moneyFormat = wrapper.dataset.moneyFormat;
    if (!input || !resultsEl || !endpoint || input.dataset.predictiveSearchReady) return;
    input.dataset.predictiveSearchReady = 'true';

    const labels = {
      products: wrapper.dataset.labelProducts,
      collections: wrapper.dataset.labelCollections,
      pages: wrapper.dataset.labelPages,
      articles: wrapper.dataset.labelArticles,
      noResults: wrapper.dataset.labelNoResults,
      viewAll: wrapper.dataset.labelViewAll,
    };

    let debounceTimer;
    let currentController;

    function hideResults() {
      resultsEl.hidden = true;
      resultsEl.innerHTML = '';
    }

    async function fetchOneType(query, type, signal) {
      const url = `${endpoint}.json?q=${encodeURIComponent(query)}&resources[type]=${type}&resources[limit]=${RESOURCE_LIMIT}&resources[options][unavailable_products]=last`;
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);
      const data = await response.json();
      return data?.resources?.results || {};
    }

    async function fetchResults(query) {
      currentController?.abort();
      currentController = new AbortController();
      const { signal } = currentController;

      // Shopify's resources[limit] caps the TOTAL across all requested
      // types combined, not per type — so a single combined request lets
      // products crowd out collections/pages/articles entirely. Fetching
      // each type separately guarantees every type gets its own budget.
      try {
        const [products, collections, pages, articles] = await Promise.all([
          fetchOneType(query, 'product', signal),
          fetchOneType(query, 'collection', signal),
          fetchOneType(query, 'page', signal),
          fetchOneType(query, 'article', signal),
        ]);

        const results = {
          products: products.products || [],
          collections: collections.collections || [],
          pages: pages.pages || [],
          articles: articles.articles || [],
        };

        resultsEl.innerHTML = renderResults(results, query, searchUrl, labels, moneyFormat);
        resultsEl.hidden = false;
      } catch (err) {
        if (err.name !== 'AbortError') hideResults();
      }
    }

    input.addEventListener('input', () => {
      const query = input.value.trim();
      clearTimeout(debounceTimer);

      if (query.length < MIN_QUERY_LENGTH) {
        currentController?.abort();
        hideResults();
        return;
      }

      debounceTimer = setTimeout(() => fetchResults(query), DEBOUNCE_MS);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') hideResults();
    });
  });
}

document.addEventListener('DOMContentLoaded', () => initPredictiveSearch(document));
document.addEventListener('shopify:section:load', (event) => initPredictiveSearch(event.target));
